/**

 * Per-tenant (university) brand theme helpers.

 * CSS vars match global.css hex format (e.g. --primary: #4f46e5).

 *

 * Token mapping:

 * - DB `primary_color`  → `--primary` (main brand, ~80% of brand-colored UI)

 * - DB `secondary_color` → `--brand-accent` (sparse highlights, ~20%)

 * - shadcn `--secondary` stays muted zinc for quiet chips/buttons — never tenant orange

 *

 * Default brand-accent: `#6366F1` (soft indigo companion to default primary).

 * Tenant orange (e.g. Vedic `#E9800B`) only appears when set in DB/config.

 */



export const DEFAULT_THEME = Object.freeze({

  primary: "#4F46E5",

  primaryForeground: "#FAFAFA",

  /** Quiet muted UI — independent of tenant accent */

  secondary: "#F4F4F5",

  secondaryForeground: "#18181B",

  /** Sparse brand highlight; soft indigo companion when tenant has no accent */

  brandAccent: "#6366F1",

  brandAccentForeground: "#FAFAFA",

  ring: "#6366F1",

});



const HEX_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;



const THEME_CACHE_PREFIX = "tenant-theme:";

const DOMAIN_CACHE_PREFIX = "tenant-theme-domain:";



/** @param {string | null | undefined} value */

export function normalizeHex(value) {

  if (typeof value !== "string") return null;

  const trimmed = value.trim();

  if (!HEX_RE.test(trimmed)) return null;



  if (trimmed.length === 4) {

    const [, r, g, b] = trimmed;

    return `#${r}${r}${g}${g}${b}${b}`.toUpperCase();

  }



  return trimmed.toUpperCase();

}



/** @param {string} hex */

function hexToRgb(hex) {

  const normalized = normalizeHex(hex);

  if (!normalized) return null;

  return {

    r: parseInt(normalized.slice(1, 3), 16),

    g: parseInt(normalized.slice(3, 5), 16),

    b: parseInt(normalized.slice(5, 7), 16),

  };

}



/** @param {{ r: number, g: number, b: number }} rgb */

function rgbToHex({ r, g, b }) {

  const to = (n) =>

    Math.max(0, Math.min(255, Math.round(n)))

      .toString(16)

      .padStart(2, "0");

  return `#${to(r)}${to(g)}${to(b)}`.toUpperCase();

}



/** Relative luminance (sRGB) for contrast decisions. */

function relativeLuminance(hex) {

  const rgb = hexToRgb(hex);

  if (!rgb) return 0;

  const channel = (c) => {

    const s = c / 255;

    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;

  };

  return (

    0.2126 * channel(rgb.r) +

    0.7152 * channel(rgb.g) +

    0.0722 * channel(rgb.b)

  );

}



/** @param {string} backgroundHex */

export function contrastForeground(backgroundHex) {

  return relativeLuminance(backgroundHex) > 0.45 ? "#18181B" : "#FAFAFA";

}



/** @param {string} hex @param {number} amount 0–1 toward black */

export function darkenHex(hex, amount = 0.12) {

  const rgb = hexToRgb(hex);

  if (!rgb) return hex;

  const factor = 1 - Math.max(0, Math.min(1, amount));

  return rgbToHex({

    r: rgb.r * factor,

    g: rgb.g * factor,

    b: rgb.b * factor,

  });

}



/** Mix color toward white for soft surfaces (e.g. indigo-50 equivalent). */

export function softSurfaceHex(hex, whiteMix = 0.9) {

  const rgb = hexToRgb(hex);

  if (!rgb) return "#EEF2FF";

  const t = Math.max(0, Math.min(1, whiteMix));

  return rgbToHex({

    r: rgb.r + (255 - rgb.r) * t,

    g: rgb.g + (255 - rgb.g) * t,

    b: rgb.b + (255 - rgb.b) * t,

  });

}



/**

 * Resolve theme from university row + optional config JSON fallbacks.

 * Invalid / missing values fall back to DEFAULT_THEME.

 *

 * Important: university.secondary_color maps to brandAccent, NOT shadcn secondary.

 */

export function resolveTenantTheme(university) {

  const config = university?.config && typeof university.config === "object"

    ? university.config

    : {};

  const themeConfig =

    config.theme && typeof config.theme === "object" ? config.theme : {};



  const primary =

    normalizeHex(university?.primary_color) ||

    normalizeHex(themeConfig.primary_color) ||

    normalizeHex(themeConfig.primary) ||

    normalizeHex(config.primary_color) ||

    DEFAULT_THEME.primary;



  // DB secondary_color → brand accent (sparse highlights), never --secondary

  const brandAccent =

    normalizeHex(university?.secondary_color) ||

    normalizeHex(themeConfig.secondary_color) ||

    normalizeHex(themeConfig.brand_accent) ||

    normalizeHex(themeConfig.brandAccent) ||

    normalizeHex(config.secondary_color) ||

    normalizeHex(config.brand_accent) ||

    DEFAULT_THEME.brandAccent;



  const primaryForeground =

    normalizeHex(university?.primary_foreground) ||

    normalizeHex(themeConfig.primary_foreground) ||

    normalizeHex(config.primary_foreground) ||

    contrastForeground(primary);



  const brandAccentForeground =

    normalizeHex(themeConfig.brand_accent_foreground) ||

    normalizeHex(themeConfig.brandAccentForeground) ||

    contrastForeground(brandAccent);



  // Quiet muted secondary stays product default (not tenant orange)

  const secondary = DEFAULT_THEME.secondary;

  const secondaryForeground = DEFAULT_THEME.secondaryForeground;



  return {

    primary,

    primaryForeground,

    secondary,

    secondaryForeground,

    brandAccent,

    brandAccentForeground,

    primaryHover: darkenHex(primary, 0.12),

    primaryMuted: softSurfaceHex(primary, 0.9),

    brandAccentHover: darkenHex(brandAccent, 0.12),

    brandAccentMuted: softSurfaceHex(brandAccent, 0.9),

    ring:

      normalizeHex(themeConfig.ring) ||

      (primary === DEFAULT_THEME.primary ? DEFAULT_THEME.ring : primary),

  };

}



function buildCssVars(theme) {

  const resolved = {

    primary: theme.primary || DEFAULT_THEME.primary,

    primaryForeground:

      theme.primaryForeground || DEFAULT_THEME.primaryForeground,

    secondary: DEFAULT_THEME.secondary,

    secondaryForeground: DEFAULT_THEME.secondaryForeground,

    brandAccent: theme.brandAccent || DEFAULT_THEME.brandAccent,

    brandAccentForeground:

      theme.brandAccentForeground || DEFAULT_THEME.brandAccentForeground,

  };



  const primaryHover =

    theme.primaryHover || darkenHex(resolved.primary, 0.12);

  const primaryMuted =

    theme.primaryMuted || softSurfaceHex(resolved.primary, 0.9);

  const brandAccentHover =

    theme.brandAccentHover || darkenHex(resolved.brandAccent, 0.12);

  const brandAccentMuted =

    theme.brandAccentMuted || softSurfaceHex(resolved.brandAccent, 0.9);

  const ring = theme.ring || DEFAULT_THEME.ring;



  return {

    "--primary": resolved.primary,

    "--primary-foreground": resolved.primaryForeground,

    "--secondary": resolved.secondary,

    "--secondary-foreground": resolved.secondaryForeground,

    "--brand-accent": resolved.brandAccent,

    "--brand-accent-foreground": resolved.brandAccentForeground,

    "--brand-accent-hover": brandAccentHover,

    "--brand-accent-muted": brandAccentMuted,

    "--ring": ring,

    "--primary-hover": primaryHover,

    "--primary-muted": primaryMuted,

    // Keep sidebar tokens in sync so shell accents follow tenant brand

    "--sidebar-primary": resolved.primary,

    "--sidebar-primary-foreground": resolved.primaryForeground,

    "--sidebar-ring": ring,

  };

}



/**

 * Apply theme CSS variables on documentElement. Never throws.

 * Accepts either a resolved theme object or raw { primary, brandAccent, ... } colors.

 * @param {Record<string, string> | null | undefined} partial

 */

export function applyTheme(partial) {

  try {

    if (typeof document === "undefined") return;



    const primary = normalizeHex(partial?.primary) || DEFAULT_THEME.primary;

    const brandAccent =

      normalizeHex(partial?.brandAccent) || DEFAULT_THEME.brandAccent;



    const cssVars = buildCssVars({

      primary,

      primaryForeground:

        normalizeHex(partial?.primaryForeground) ||

        contrastForeground(primary),

      brandAccent,

      brandAccentForeground:

        normalizeHex(partial?.brandAccentForeground) ||

        contrastForeground(brandAccent),

      primaryHover: normalizeHex(partial?.primaryHover),

      primaryMuted: normalizeHex(partial?.primaryMuted),

      brandAccentHover: normalizeHex(partial?.brandAccentHover),

      brandAccentMuted: normalizeHex(partial?.brandAccentMuted),

      ring: normalizeHex(partial?.ring) || DEFAULT_THEME.ring,

    });



    const root = document.documentElement;

    for (const [key, value] of Object.entries(cssVars)) {

      root.style.setProperty(key, value);

    }

  } catch (error) {

    console.warn("[tenantTheme] applyTheme failed; keeping defaults", error);

  }

}



/** Apply defaults explicitly (safe recovery). */

export function applyDefaultTheme() {

  applyTheme({ ...DEFAULT_THEME });

}



function cacheKeyForTenant(tenantKey) {

  return `${THEME_CACHE_PREFIX}${tenantKey}`;

}



function cacheKeyForDomain(domain) {

  return `${DOMAIN_CACHE_PREFIX}${domain}`;

}



function getCurrentDomain() {

  if (typeof window === "undefined") return null;

  return (

    (import.meta.env.MODE === "development"

      ? import.meta.env.VITE_THEME_DEV_DOMAIN || "www.vedic.dev"

      : window.location.host) || null

  );

}



/**

 * Persist theme colors only (no secrets). Keys by university id + domain.

 */

export function cacheTenantTheme(university, theme) {

  try {

    if (typeof localStorage === "undefined" || !university?.id) return;



    const payload = {

      universityId: university.id,

      tenantId: university.tenantId || null,

      domain: university.domain || getCurrentDomain(),

      primary: theme.primary,

      primaryForeground: theme.primaryForeground,

      secondary: DEFAULT_THEME.secondary,

      secondaryForeground: DEFAULT_THEME.secondaryForeground,

      brandAccent: theme.brandAccent,

      brandAccentForeground: theme.brandAccentForeground,

      primaryHover: theme.primaryHover,

      primaryMuted: theme.primaryMuted,

      brandAccentHover: theme.brandAccentHover,

      brandAccentMuted: theme.brandAccentMuted,

      ring: theme.ring,

      updatedAt: Date.now(),

    };



    const serialized = JSON.stringify(payload);

    localStorage.setItem(cacheKeyForTenant(university.id), serialized);



    if (university.tenantId) {

      localStorage.setItem(

        cacheKeyForTenant(university.tenantId),

        serialized

      );

    }



    const domain = payload.domain || getCurrentDomain();

    if (domain) {

      localStorage.setItem(cacheKeyForDomain(domain.toLowerCase()), serialized);

    }

  } catch (error) {

    console.warn("[tenantTheme] cache write failed", error);

  }

}



/**

 * Resolve brand accent from cache, including legacy payloads where

 * `secondary` held the tenant accent (orange) before the token remap.

 */

function resolveBrandAccentFromCache(parsed) {

  const explicit = normalizeHex(parsed.brandAccent);

  if (explicit) return explicit;



  const legacySecondary = normalizeHex(parsed.secondary);

  if (

    legacySecondary &&

    legacySecondary !== DEFAULT_THEME.secondary

  ) {

    return legacySecondary;

  }



  return DEFAULT_THEME.brandAccent;

}



function readCachedThemePayload() {

  try {

    if (typeof localStorage === "undefined") return null;



    const domain = getCurrentDomain();

    if (!domain) return null;



    const raw = localStorage.getItem(cacheKeyForDomain(domain.toLowerCase()));

    if (!raw) return null;



    const parsed = JSON.parse(raw);

    if (!parsed || typeof parsed !== "object") return null;



    const primary = normalizeHex(parsed.primary) || DEFAULT_THEME.primary;

    const brandAccent = resolveBrandAccentFromCache(parsed);



    return {

      primary,

      primaryForeground:

        normalizeHex(parsed.primaryForeground) ||

        contrastForeground(primary),

      secondary: DEFAULT_THEME.secondary,

      secondaryForeground: DEFAULT_THEME.secondaryForeground,

      brandAccent,

      brandAccentForeground:

        normalizeHex(parsed.brandAccentForeground) ||

        contrastForeground(brandAccent),

      primaryHover: normalizeHex(parsed.primaryHover),

      primaryMuted: normalizeHex(parsed.primaryMuted),

      brandAccentHover: normalizeHex(parsed.brandAccentHover),

      brandAccentMuted: normalizeHex(parsed.brandAccentMuted),

      ring: normalizeHex(parsed.ring) || DEFAULT_THEME.ring,

    };

  } catch {

    return null;

  }

}



/**

 * Early boot: apply cached theme for current host before React paints.

 * Call from main.jsx as early as practical.

 */

export function applyCachedThemeForCurrentHost() {

  try {

    const cached = readCachedThemePayload();

    if (cached) {

      applyTheme(cached);

    }

  } catch (error) {

    console.warn("[tenantTheme] cached apply failed", error);

  }

}



/**

 * Resolve, apply, and cache theme from university bootstrap payload.

 * Safe when fields are missing/invalid.

 */

export function syncUniversityTheme(university) {

  try {

    if (!university) {

      applyDefaultTheme();

      return { ...DEFAULT_THEME };

    }



    const theme = resolveTenantTheme(university);

    applyTheme(theme);

    cacheTenantTheme(university, theme);

    return theme;

  } catch (error) {

    console.warn("[tenantTheme] sync failed; using defaults", error);

    applyDefaultTheme();

    return { ...DEFAULT_THEME };

  }

}


